import { 
  Activity,
  AlertTriangle,
  ArrowRight,
  Building, 
  Calendar, 
  CheckSquare, 
  Clock, 
  Plus,
  TrendingUp} from 'lucide-react';
import { useEffect,useState } from 'react';
import { Link } from 'react-router-dom';

import QuickTaskCreator from '@features/tarefas/components/QuickTaskCreator';

import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';


interface DashboardStats {
  totalObras: number;
  obrasAtivas: number;
  totalTarefas: number;
  tarefasVencidas: number;
  tarefasHoje: number;
  tarefasAndamento: number;
}

interface TarefaRecente {
  id: string;
  nome: string;
  status: string;
  data_limite: string;
  obra_nome?: string;
  responsavel_nome?: string;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalObras: 0,
    obrasAtivas: 0,
    totalTarefas: 0,
    tarefasVencidas: 0,
    tarefasHoje: 0,
    tarefasAndamento: 0
  });
  const [tarefasRecentes, setTarefasRecentes] = useState<TarefaRecente[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQuickTask, setShowQuickTask] = useState(false);

  useEffect(() => {
    carregarDashboard();
  }, [user]);

  async function carregarDashboard() {
    try {
      setLoading(true);

      // Carregar estatísticas de obras
      const { data: obras } = await supabase
        .from('obras')
        .select('id, status')
        .eq('ativo', true);

      // Carregar estatísticas de tarefas
      const { data: tarefas } = await supabase
        .from('tarefas')
        .select(`
          id, 
          nome, 
          status, 
          data_limite,
          responsavel_id,
          obras(nome),
          usuarios(nome)
        `)
        .eq('ativo', true)
        .order('created_at', { ascending: false })
        .limit(10);

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const statsCalculadas: DashboardStats = {
        totalObras: obras?.length || 0,
        obrasAtivas: obras?.filter(o => o.status === 'em_andamento').length || 0,
        totalTarefas: tarefas?.length || 0,
        tarefasVencidas: tarefas?.filter(t => {
          if (!t.data_limite || t.status === 'concluido') return false;
          return new Date(t.data_limite) < hoje;
        }).length || 0,
        tarefasHoje: tarefas?.filter(t => {
          if (!t.data_limite) return false;
          const vencimento = new Date(t.data_limite);
          vencimento.setHours(0, 0, 0, 0);
          return vencimento.getTime() === hoje.getTime();
        }).length || 0,
        tarefasAndamento: tarefas?.filter(t => t.status === 'em_andamento').length || 0
      };

      const tarefasFormatadas: TarefaRecente[] = (tarefas || []).slice(0, 5).map(t => ({
        id: t.id,
        nome: t.nome,
        status: t.status,
        data_limite: t.data_limite,
        obra_nome: (t.obras as any)?.nome,
        responsavel_nome: (t.usuarios as any)?.nome
      }));

      setStats(statsCalculadas);
      setTarefasRecentes(tarefasFormatadas);

    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'nao_iniciado': return 'text-yellow-600 dark:text-yellow-400';
      case 'em_andamento': return 'text-blue-600 dark:text-blue-400';
      case 'concluido': return 'text-green-600 dark:text-green-400';
      case 'parado': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'nao_iniciado': return 'Não Iniciado';
      case 'em_andamento': return 'Em Andamento';
      case 'concluido': return 'Concluído';
      case 'parado': return 'Parado';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    const vencimento = new Date(dateString);
    vencimento.setHours(0, 0, 0, 0);
    
    if (vencimento.getTime() === hoje.getTime()) {
      return 'Hoje';
    } else if (vencimento < hoje) {
      const diasAtrasado = Math.floor((hoje.getTime() - vencimento.getTime()) / (1000 * 60 * 60 * 24));
      return `${diasAtrasado}d atrasado`;
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background dark:bg-gray-900 transition-colors">
      <QuickTaskCreator
        isOpen={showQuickTask}
        onClose={() => setShowQuickTask(false)}
        onSuccess={carregarDashboard}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-ageo-bold text-foreground dark:text-gray-100">
                Olá, {user?.user_metadata?.nome || 'Usuário'}! 👋
              </h1>
              <p className="text-gray-600 dark:text-gray-400 font-ageo-regular mt-1">
                Bem-vindo ao painel da ACOR Engenharia
              </p>
            </div>
            <button
              onClick={() => setShowQuickTask(true)}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-confirmation text-white rounded-lg hover:bg-confirmation/90 transition-colors font-ageo-regular"
            >
              <Plus className="w-4 h-4" />
              Nova Tarefa
            </button>
          </div>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          
          {/* Total de Obras */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-secondary dark:border-gray-700 transition-colors">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Building className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-ageo-bold text-foreground dark:text-gray-100">{stats.totalObras}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-ageo-regular">Obras</p>
            </div>
          </div>

          {/* Obras Ativas */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-secondary dark:border-gray-700 transition-colors">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-ageo-bold text-foreground dark:text-gray-100">{stats.obrasAtivas}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-ageo-regular">Ativas</p>
            </div>
          </div>

          {/* Total de Tarefas */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-secondary dark:border-gray-700 transition-colors">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <CheckSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-ageo-bold text-foreground dark:text-gray-100">{stats.totalTarefas}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-ageo-regular">Tarefas</p>
            </div>
          </div>

          {/* Tarefas Vencidas */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-secondary dark:border-gray-700 transition-colors">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-ageo-bold text-foreground dark:text-gray-100">{stats.tarefasVencidas}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-ageo-regular">Vencidas</p>
            </div>
          </div>

          {/* Tarefas Hoje */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-secondary dark:border-gray-700 transition-colors">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-ageo-bold text-foreground dark:text-gray-100">{stats.tarefasHoje}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-ageo-regular">Hoje</p>
            </div>
          </div>

          {/* Em Andamento */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-secondary dark:border-gray-700 transition-colors">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-2xl font-ageo-bold text-foreground dark:text-gray-100">{stats.tarefasAndamento}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-ageo-regular">Andamento</p>
            </div>
          </div>
        </div>

        {/* Grid Principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Tarefas Recentes */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-secondary dark:border-gray-700 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-ageo-bold text-foreground dark:text-gray-100">Tarefas Recentes</h2>
                <Link
                  to="/minhas-tarefas"
                  className="text-sm text-primary dark:text-blue-400 hover:text-primary/80 font-ageo-regular flex items-center gap-1"
                >
                  Ver todas
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              
              <div className="space-y-3">
                {tarefasRecentes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <CheckSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="font-ageo-regular">Nenhuma tarefa encontrada</p>
                  </div>
                ) : (
                  tarefasRecentes.map((tarefa) => (
                    <div
                      key={tarefa.id}
                      className="flex items-center justify-between p-3 hover:bg-secondary/20 dark:hover:bg-gray-700/50 rounded-lg transition-colors cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-ageo-regular text-foreground dark:text-gray-100 truncate">
                          {tarefa.nome}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs font-ageo-regular ${getStatusColor(tarefa.status)}`}>
                            {getStatusLabel(tarefa.status)}
                          </span>
                          {tarefa.obra_nome && (
                            <>
                              <span className="text-gray-400">•</span>
                              <span className="text-xs text-gray-600 dark:text-gray-400 font-ageo-regular">
                                {tarefa.obra_nome}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {tarefa.data_limite && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 font-ageo-regular">
                          {formatDate(tarefa.data_limite)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Ações Rápidas */}
          <div className="space-y-6">
            
            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-secondary dark:border-gray-700 transition-colors">
              <h2 className="text-lg font-ageo-bold text-foreground dark:text-gray-100 mb-4">Ações Rápidas</h2>
              
              <div className="space-y-3">
                <button
                  onClick={() => setShowQuickTask(true)}
                  className="w-full flex items-center gap-3 p-3 bg-confirmation/10 dark:bg-green-900/20 text-confirmation dark:text-green-400 rounded-lg hover:bg-confirmation/20 dark:hover:bg-green-900/30 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-ageo-regular">Nova Tarefa</span>
                </button>
                
                <Link
                  to="/obras/nova"
                  className="w-full flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <Building className="w-5 h-5" />
                  <span className="font-ageo-regular">Nova Obra</span>
                </Link>
                
                <Link
                  to="/diarios/novo"
                  className="w-full flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                >
                  <Calendar className="w-5 h-5" />
                  <span className="font-ageo-regular">Novo Diário</span>
                </Link>
              </div>
            </div>

            {/* Performance */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-secondary dark:border-gray-700 transition-colors">
              <h2 className="text-lg font-ageo-bold text-foreground dark:text-gray-100 mb-4">Performance</h2>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-ageo-regular text-gray-600 dark:text-gray-400">Tarefas Concluídas</span>
                    <span className="text-sm font-ageo-bold text-foreground dark:text-gray-100">85%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-ageo-regular text-gray-600 dark:text-gray-400">Obras no Prazo</span>
                    <span className="text-sm font-ageo-bold text-foreground dark:text-gray-100">92%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: '92%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}